import type { FloorKind, WallStyle } from '@/gfx/tiles';

export type FloorId = 'basement' | 'ground' | 'second' | 'third' | 'attic';
export const FLOOR_ORDER: FloorId[] = ['basement', 'ground', 'second', 'third', 'attic'];
export const FLOOR_NAMES: Record<FloorId, string> = {
  basement: '지하', ground: '1층', second: '2층', third: '3층', attic: '다락',
};

export type RoomKind =
  | 'hall' | 'corridor' | 'stairhall' | 'bedroom' | 'guest' | 'master' | 'study' | 'library' | 'secret' | 'dining' | 'kitchen' | 'pantry'
  | 'bath' | 'washroom' | 'ballroom' | 'conservatory' | 'music' | 'billiard' | 'drawing' | 'smoking' | 'cloak' | 'passage'
  | 'cellar' | 'boiler' | 'laundry' | 'storage' | 'cell' | 'well' | 'tunnel' | 'coal' | 'ice'
  | 'gallery' | 'nursery' | 'sewing' | 'dressing' | 'closet' | 'balcony'
  | 'servant' | 'sick' | 'archive' | 'doll' | 'clock' | 'observatory' | 'chapel' | 'schoolroom' | 'linen'
  | 'attic' | 'tank' | 'lair' | 'trophy' | 'empty';

export interface PropPlacement {
  kind: string;
  x: number; // tile, top-left of footprint (absolute floor coords)
  y: number;
  id?: string;
  flip?: boolean;
  /** free-form per-prop data (e.g. safe code id, lever target, note text id) */
  data?: Record<string, unknown>;
}

export type DecorKind = 'painting' | 'paintingBig' | 'mirror' | 'window' | 'windowBig' | 'sconce' | 'fusebox' | 'hook' | 'shelf' | 'clockWall' | 'antlers' | 'crack' | 'poster' | 'cross' | 'vent' | 'plaque' | 'crawlspace';
export interface DecorPlacement {
  kind: DecorKind;
  x: number; // wall tile coords (the wall tile whose face is visible)
  y: number;
  id?: string;
  data?: Record<string, unknown>;
}

export interface LightDef {
  x: number; // px, absolute floor coords
  y: number;
  r: number;
  color: string;
  flicker?: boolean;
  /** only lit when the fuse box is powered */
  powered?: boolean;
  /** window moonlight cone pointing down */
  cone?: { angle: number; half: number };
  id?: string;
}

export interface RoomDef {
  id: string;
  name: string;
  x: number; y: number; w: number; h: number; // inner floor rect (tiles)
  floor: FloorKind;
  wall: WallStyle;
  kind: RoomKind;
  props?: PropPlacement[];
  decor?: DecorPlacement[];
  lights?: LightDef[];
  /** auto-furnish with the kind's template (default: true unless corridor/hall/empty) */
  furnish?: boolean;
  /** fraction 0..1 of the north wall that gets windows (auto) */
  windows?: number;
  /** rooms that can serve as a prison this run */
  prison?: boolean;
  /** hint text shown in the HUD when entered */
  note?: string;
}

export type DoorKind = 'open' | 'door' | 'double' | 'locked' | 'boarded' | 'secret' | 'cell' | 'gate' | 'exit' | 'bolt' | 'arch';
export interface DoorDef {
  id?: string;
  a: string;
  b: string;
  kind: DoorKind;
  /** item id required for 'locked' doors */
  key?: string;
  /** 0..1 position along the shared wall (default 0.5) */
  at?: number;
  /** flag id that opens 'secret' / 'gate' doors */
  trigger?: string;
  label?: string;
}

export interface StairsDef {
  id: string;
  room: string;
  x: number; y: number; // tile top-left
  w?: number; h?: number; // footprint (default 2x3)
  dir: 'up' | 'down';
  /** side you step onto the stairs from */
  enter: 'N' | 'S' | 'E' | 'W';
  /** linked stairs id on another floor */
  to: string;
  toFloor: FloorId;
  /** ladder/hatch variant */
  kind?: 'stairs' | 'ladder';
}

export interface FloorDef {
  id: FloorId;
  w: number;
  h: number;
  rooms: RoomDef[];
  doors: DoorDef[];
  stairs: StairsDef[];
}

export interface MansionDef {
  floors: FloorDef[];
}

// tile codes
export const T_VOID = 0;
export const T_WALL = 1;
export const T_FLOOR = 2;
export const T_DOOR = 3;
export const T_STAIRS = 4;
